<div class="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground lg:flex-row">
  <div class="flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden lg:w-1/2">
    <header class="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4 sm:h-16 sm:px-8">
      <div class="flex min-w-0 items-center gap-3">
        @if (showBack()) {
          <button type="button" class="btn-shout-fab-secondary" (click)="backToSignIn()" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="size-5" aria-hidden="true">
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
          </button>
        }
        <img src="/assets/logos/official/SHOUT_studio_logo_full_colour_black.svg" alt="SHOUT Studio" class="h-7 w-auto dark:hidden" />
        <img src="/assets/logos/official/SHOUT_studio_logo_full_colour_white.svg" alt="" class="hidden h-7 w-auto dark:block" />
      </div>
      @if (step() === 'signin') {
        <a routerLink="/onboarding-select" class="inline-flex h-10 shrink-0 items-center rounded-full border border-border px-4 text-base font-bold text-foreground hover:border-primary hover:text-primary sm:hidden">Free trial</a>
        <a routerLink="/onboarding-select" class="hidden h-10 shrink-0 items-center rounded-full border border-border px-4 text-base font-bold text-foreground hover:border-primary hover:text-primary sm:inline-flex">Start free trial</a>
      }
    </header>

    <main class="mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col overflow-y-auto px-5 py-8 pb-40 sm:px-8 sm:pb-12 lg:py-16">
      @switch (step()) {
        @case ('signin') {
          <form class="flex flex-1 flex-col gap-6" (submit)="onSignInSubmit($event)" novalidate>
            <div class="flex flex-col gap-3">
              <p class="m-0 text-sm font-bold uppercase tracking-widest text-primary">Welcome back</p>
              <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">Sign in to <span class="text-primary">SHOUT.</span></h1>
              <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">Pick up your campaigns right where you left them.</p>
            </div>

            <button type="button" class="btn-shout btn-shout-md btn-shout-secondary h-14 w-full justify-center" (click)="continueWithGoogle()" [attr.aria-busy]="googlePending()">
              @if (googlePending()) {
                <span class="size-4 animate-spin rounded-full border-2 border-border border-t-foreground" aria-hidden="true"></span>
                Connecting to Google…
              } @else {
                <img src="/assets/google-sign-in/google-icon-logo.svg" alt="" class="size-5" />
                Continue with Google
              }
            </button>

            <div class="flex items-center gap-3">
              <div class="h-px flex-1 bg-border" aria-hidden="true"></div>
              <p class="m-0 text-sm font-bold tracking-wide text-muted-foreground">OR WITH EMAIL</p>
              <div class="h-px flex-1 bg-border" aria-hidden="true"></div>
            </div>

            @if (authError(); as message) {
              <div role="alert" class="flex items-start gap-3 rounded-lg border border-red-600/30 bg-red-50 px-4 py-3.5 dark:bg-red-950/40">
                <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-black text-white" aria-hidden="true">!</span>
                <p class="m-0 text-base font-semibold leading-snug text-red-900 dark:text-red-200">
                  @if (authErrorOffersReset()) {
                    That email and password don't match. Try again or
                    <button type="button" class="font-bold underline" (click)="goToForgot()">reset your password</button>.
                  } @else {
                    {{ message }}
                  }
                </p>
              </div>
            }

            <div class="flex flex-col gap-5">
              <div class="flex flex-col gap-2">
                <label for="email" class="text-base font-bold">Email</label>
                <input
                  id="email"
                  type="text"
                  inputmode="email"
                  autocomplete="email"
                  autocapitalize="none"
                  placeholder="you@company.com"
                  class="h-14 w-full rounded-lg border bg-card px-4 text-lg font-semibold text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  [class.border-red-600]="emailInvalid()"
                  [class.border-border]="!emailInvalid()"
                  [attr.aria-invalid]="emailInvalid()"
                  [attr.aria-describedby]="fieldErrors().email ? 'email-error' : null"
                  [disabled]="isLoading() || googlePending()"
                  [value]="loginModel().email"
                  (input)="onEmailInput($event)" />
                @if (fieldErrors().email) {
                  <p id="email-error" class="m-0 text-base font-semibold text-red-600">{{ fieldErrors().email }}</p>
                }
              </div>

              <div class="flex flex-col gap-2">
                <div class="flex items-baseline justify-between gap-3">
                  <label for="password" class="text-base font-bold">Password</label>
                  <button type="button" class="text-base font-bold text-primary hover:text-primary/80" (click)="goToForgot()">Forgot password?</button>
                </div>
                <div
                  class="flex h-14 overflow-hidden rounded-lg border bg-card focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
                  [class.border-red-600]="passwordInvalid()"
                  [class.border-border]="!passwordInvalid()">
                  <input
                    id="password"
                    [type]="passwordVisible() ? 'text' : 'password'"
                    autocomplete="current-password"
                    placeholder="Your password"
                    class="min-w-0 flex-1 border-0 bg-transparent px-4 text-lg font-semibold text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    [attr.aria-invalid]="passwordInvalid()"
                    [attr.aria-describedby]="fieldErrors().password ? 'password-error' : null"
                    [disabled]="isLoading() || googlePending()"
                    [value]="loginModel().password"
                    (input)="onPasswordInput($event)" />
                  <button
                    type="button"
                    class="flex w-14 shrink-0 items-center justify-center border-l border-border bg-muted text-muted-foreground hover:text-primary"
                    [attr.aria-label]="passwordVisible() ? 'Hide password' : 'Show password'"
                    (click)="togglePasswordVisible()">
                    @if (passwordVisible()) {
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                        <path d="M4 4l16 16" />
                      </svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    }
                  </button>
                </div>
                @if (fieldErrors().password) {
                  <p id="password-error" class="m-0 text-base font-semibold text-red-600">{{ fieldErrors().password }}</p>
                }
              </div>
            </div>

            <div class="login-cta-safe fixed inset-x-0 bottom-0 z-20 mt-auto flex flex-col gap-3 border-t border-border bg-background px-5 py-3 sm:static sm:z-auto sm:mt-2 sm:border-0 sm:bg-transparent sm:p-0">
              <button type="submit" class="btn-shout btn-shout-md btn-shout-primary h-14 w-full justify-center" [attr.aria-busy]="isLoading()">
                @if (isLoading()) {
                  <span class="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true"></span>
                  Signing in…
                } @else {
                  Sign in
                }
              </button>
              <p class="m-0 text-center text-base font-semibold text-muted-foreground">
                New to SHOUT?
                <a routerLink="/onboarding-select" class="font-bold text-primary hover:text-primary/80">Start your free trial</a>
              </p>
            </div>
          </form>

          @if (devQuickLogin.enabled) {
            <app-dev-quick-login class="mt-6" [busy]="isLoading() || googlePending()" [embedded]="true" (selectUser)="signInAs($event)"></app-dev-quick-login>
          }
        }
        @case ('forgot') {
          <form class="flex flex-1 flex-col gap-6" (submit)="onResetSubmit($event)" novalidate>
            <div class="flex flex-col gap-3">
              <p class="m-0 text-sm font-bold uppercase tracking-widest text-primary">Reset password</p>
              <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">Locked out? <span class="text-primary">We've got you.</span></h1>
              <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">Enter the email on your account and we'll send you a secure reset link.</p>
            </div>

            @if (authError(); as message) {
              <div role="alert" class="flex items-start gap-3 rounded-lg border border-red-600/30 bg-red-50 px-4 py-3.5 dark:bg-red-950/40">
                <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-black text-white" aria-hidden="true">!</span>
                <p class="m-0 text-base font-semibold leading-snug text-red-900 dark:text-red-200">{{ message }}</p>
              </div>
            }

            <div class="flex flex-col gap-2">
              <label for="reset-email" class="text-base font-bold">Email</label>
              <input
                id="reset-email"
                type="email"
                inputmode="email"
                autocomplete="email"
                autocapitalize="none"
                placeholder="you@company.com"
                class="h-14 w-full rounded-lg border bg-card px-4 text-lg font-semibold text-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                [class.border-red-600]="emailInvalid()"
                [class.border-border]="!emailInvalid()"
                [attr.aria-invalid]="emailInvalid()"
                [attr.aria-describedby]="fieldErrors().email ? 'reset-email-error' : null"
                [disabled]="isResettingPassword()"
                [value]="loginModel().email"
                (input)="onEmailInput($event)" />
              @if (fieldErrors().email) {
                <p id="reset-email-error" class="m-0 text-base font-semibold text-red-600">{{ fieldErrors().email }}</p>
              }
            </div>

            <div class="login-cta-safe fixed inset-x-0 bottom-0 z-20 mt-auto flex flex-col gap-3 border-t border-border bg-background px-5 py-3 sm:static sm:z-auto sm:mt-2 sm:border-0 sm:bg-transparent sm:p-0">
              <button type="submit" class="btn-shout btn-shout-md btn-shout-primary h-14 w-full justify-center" [attr.aria-busy]="isResettingPassword()">
                @if (isResettingPassword()) {
                  <span class="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true"></span>
                  Sending link…
                } @else {
                  Send reset link
                }
              </button>
              <button type="button" class="min-h-11 text-base font-bold text-primary hover:text-primary/80" (click)="backToSignIn()">Back to sign in</button>
            </div>
          </form>
        }
        @case ('sent') {
          <div class="flex flex-1 flex-col gap-6">
            <span class="flex size-16 items-center justify-center rounded-xl bg-linear-to-br from-brand-deep-purple to-primary" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" class="size-7 text-white">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="m3 7 9 6 9-6" />
              </svg>
            </span>
            <div class="flex flex-col gap-3">
              <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">Check your <span class="text-primary">inbox.</span></h1>
              <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">
                We've sent a reset link to <strong class="text-foreground">{{ loginModel().email }}</strong>. It expires in 30 minutes.
              </p>
            </div>
            <div class="flex flex-col gap-1.5 rounded-lg bg-primary/10 p-4 text-brand-agency-dark">
              <p class="m-0 text-base font-bold">Can't find it?</p>
              <p class="m-0 text-base font-semibold leading-snug">Check your spam folder, or make sure you used the email you signed up with.</p>
            </div>

            @if (authError(); as message) {
              <div role="alert" class="flex items-start gap-3 rounded-lg border border-red-600/30 bg-red-50 px-4 py-3.5 dark:bg-red-950/40">
                <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-black text-white" aria-hidden="true">!</span>
                <p class="m-0 text-base font-semibold leading-snug text-red-900 dark:text-red-200">{{ message }}</p>
              </div>
            }

            <div class="login-cta-safe fixed inset-x-0 bottom-0 z-20 mt-auto flex flex-col gap-3 border-t border-border bg-background px-5 py-3 sm:static sm:z-auto sm:mt-2 sm:border-0 sm:bg-transparent sm:p-0">
              <button type="button" class="btn-shout btn-shout-md btn-shout-primary h-14 w-full justify-center" (click)="backToSignIn()">Back to sign in</button>
              <button
                type="button"
                [class]="resendClass()"
                [disabled]="resetResent() || isResettingPassword()"
                (click)="resendReset()">
                @if (isResettingPassword()) {
                  Sending link…
                } @else if (resetResent()) {
                  Link resent ✓
                } @else {
                  Resend link
                }
              </button>
            </div>
          </div>
        }
      }
    </main>
  </div>
  <login-brand-panel />
</div>
